import React, { useState, useEffect } from 'react';
import { IntegrationField } from '../types';
import { getJiraFields } from '../../../api';
import HelpIcon from './HelpIcon';

// Define custom field types for Jira
type JiraCustomFieldType = 'sprintFieldId' | 'storyPointsFieldId';

interface JiraFieldSelectorComponentProps {
  field: IntegrationField;
  value: string;
  error?: string;
  integrationTypeId: number;
  customerIntegrationId: number | null;
  onChange: (name: string, value: string) => void;
  configValues: Record<string, any>;
}

interface JiraField {
  id: string;
  name: string;
  type: string;
  custom: boolean;
}

const JiraFieldSelectorComponent: React.FC<JiraFieldSelectorComponentProps> = ({
  field,
  value,
  error,
  integrationTypeId,
  customerIntegrationId,
  onChange,
  configValues
}) => {
  const [jiraFields, setJiraFields] = useState<JiraField[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const commonLabelStyles = {
    display: 'block',
    marginBottom: 8,
    fontWeight: 500 as const
  };

  const commonSelectStyles = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 4,
    border: `1px solid ${error ? '#f44336' : '#ccc'}`,
    fontSize: 14,
    boxSizing: 'border-box' as const
  };

  const errorMessageStyles = {
    color: '#f44336',
    fontSize: 12,
    marginTop: 4
  };

  const fetchJiraFields = async () => {
    if (!customerIntegrationId && (!configValues.jiraUrl || !configValues.email || !configValues.apiToken)) {
      // Don't try to fetch fields if we don't have the necessary config values
      setLoadError('Please enter valid details for Jira URL, Email, and API Token first');
      return;
    }

    if (jiraFields.length > 0) {
      setLoading(false);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      // If we have a customerIntegrationId, use that to fetch fields
      // Otherwise, use the current config values for validation
      const { fields } = await getJiraFields(
        integrationTypeId,
        configValues.jiraUrl,
        configValues.email,
        configValues.apiToken
      );

      setJiraFields(fields);
    } catch (err) {
      console.error('Failed to load Jira fields:', err);
      setLoadError('Failed to load Jira fields. Please check you have entered valid credentials above');
    } finally {
      setLoading(false);
    }
  };

  // Filter fields based on field type
  const getFilteredFields = () => {
    switch (field.name as JiraCustomFieldType) {
      case 'sprintFieldId':
        // Filter for sprint-related fields (typically custom fields that contain "sprint")
        return jiraFields.filter(f => 
          f.custom && 
          !f.name.startsWith('${') && 
          !f.name.startsWith('[CHART')
        );
      case 'storyPointsFieldId':
        // Filter for story points fields (typically numeric custom fields)
        return jiraFields.filter(f => 
          f.custom && 
          !f.name.startsWith('${') && 
          !f.name.startsWith('[CHART')
        );
      default:
        return jiraFields;
    }
  };

  const getFieldDisplayName = (fieldId: string) => {
    const field = jiraFields.find(f => f.id === fieldId);
    return field ? `${field.name} (${field.id})` : fieldId;
  };

  // Render the appropriate field based on the field name
  const renderCustomField = () => {
    const filteredFields = getFilteredFields();

    // If we have a value and not editing, show as label with change button
    if (value && !isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            ...commonSelectStyles,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
            flex: 1
          }}>
            {getFieldDisplayName(value)}
          </div>
          <button
            onClick={() => {
              setIsEditing(true);
              fetchJiraFields();
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Change
          </button>
        </div>
      );
    }

    // If we have no fields and we're not loading, show a button to fetch them
    if (jiraFields.length === 0 && !loading && !value) {
      return (
        <div>
          <button
            onClick={fetchJiraFields}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left'
            }}
          >
            Click to load Jira fields
          </button>
          {loadError && <div style={{ marginTop: 8, fontSize: 12, color: '#f44336' }}>{loadError}</div>}
        </div>
      );
    }

    // Otherwise show the dropdown
    return (
      <div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: loading || loadError ? '8px' : '0' }}>
          <select
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => {
              onChange(field.name, e.target.value);
              setIsEditing(false);
            }}
            required={field.required}
            style={{ ...commonSelectStyles, flex: 1 }}
            disabled={loading}
          >
            <option value="">
              {field.name === 'sprintFieldId' ? 'Select a sprint field' : 'Select a story points field'}
            </option>
            {filteredFields.map((jiraField) => (
              <option key={jiraField.id} value={jiraField.id}>
                {jiraField.name} ({jiraField.id})
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setIsEditing(false);
              if (value) {
                // If we had a previous value, restore it
                onChange(field.name, value);
              }
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
        {loading && <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>Loading Jira fields...</div>}
        {loadError && <div style={{ marginTop: 8, fontSize: 12, color: '#f44336' }}>{loadError}</div>}
        {filteredFields.length === 0 && jiraFields.length > 0 && !loading && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>
            No matching fields found. You may need to create a custom field in Jira or select from all fields.
          </div>
        )}
      </div>
    );
  };

  // Render the field with common wrapper
  return (
    <div key={field.name} style={{ marginBottom: 16 }}>
      <label htmlFor={field.name} style={{ ...commonLabelStyles, display: 'flex', alignItems: 'center' }}>
        <span>
          {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
        </span>
        {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
      </label>
      {renderCustomField()}
      {error && <div style={errorMessageStyles}>{error}</div>}
    </div>
  );
};

export default JiraFieldSelectorComponent;
